import { useEffect, useLayoutEffect, useRef, useCallback } from 'react';

const SCROLL_THRESHOLD = 100;
const USER_MSG_TOP_PADDING = 16;
const CONTENT_BOTTOM_PADDING = 24;
const PROGRAMMATIC_IGNORE_MS = 150;

export function useAutoScroll(
  messages: { role: string }[],
  streamingContent?: string
) {
  const ref = useRef<HTMLDivElement>(null);
  const userScrolledUp = useRef(false);
  const programmaticUntil = useRef(0);
  const streamingWasActive = useRef(false);

  // Separate length trackers for the two layout effects so they don't interfere
  const prevLenLayout = useRef(0); // for user-message layout effect
  const prevLenEffect = useRef(0); // for AI-message async effect

  const isNearBottom = useCallback(() => {
    const el = ref.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight <= SCROLL_THRESHOLD;
  }, []);

  const scrollToEl = useCallback((container: HTMLElement, el: HTMLElement) => {
    const cRect = container.getBoundingClientRect();
    const eRect = el.getBoundingClientRect();
    const needed = container.scrollTop + (eRect.bottom - cRect.bottom) + CONTENT_BOTTOM_PADDING;
    if (needed > container.scrollTop) {
      programmaticUntil.current = Date.now() + PROGRAMMATIC_IGNORE_MS;
      container.scrollTop = needed;
    }
  }, []);

  // ── Track user scroll (ignore programmatic scrolls) ──────────────────────
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const handleScroll = () => {
      if (Date.now() < programmaticUntil.current) return;
      if (!isNearBottom()) {
        userScrolledUp.current = true;
      } else {
        userScrolledUp.current = false;
      }
    };
    const handleWheel = (e: WheelEvent) => {
      if (e.deltaY < 0) {
        userScrolledUp.current = true;
      }
    };
    el.addEventListener('scroll', handleScroll, { passive: true });
    el.addEventListener('wheel', handleWheel, { passive: true });
    return () => {
      el.removeEventListener('scroll', handleScroll);
      el.removeEventListener('wheel', handleWheel);
    };
  }, [isNearBottom]);

  // ── User message: position synchronously before paint ────────────────────
  // Pins the view to the new question and stays there — the response is
  // never auto-followed while streaming, so the user can read at their own
  // pace without the page yanking down to the bottom.
  useLayoutEffect(() => {
    const len = messages.length;
    const prev = prevLenLayout.current;
    prevLenLayout.current = len;

    if (len <= prev) return; // reset or unchanged
    const lastMsg = messages[len - 1];
    if (lastMsg?.role !== 'user') return;

    const container = ref.current;
    if (!container) return;

    userScrolledUp.current = false;

    const userMsgs = container.querySelectorAll('.chat-msg--user');
    const lastUserEl = userMsgs[userMsgs.length - 1] as HTMLElement | undefined;
    if (lastUserEl) {
      const cRect = container.getBoundingClientRect();
      const mRect = lastUserEl.getBoundingClientRect();
      programmaticUntil.current = Date.now() + PROGRAMMATIC_IGNORE_MS;
      container.scrollTop = Math.max(0, container.scrollTop + (mRect.top - cRect.top) - USER_MSG_TOP_PADDING);
    }
  }, [messages]);

  // ── AI message: skip scroll after stream, scroll for history loads ────────
  useEffect(() => {
    const len = messages.length;
    const prev = prevLenEffect.current;
    prevLenEffect.current = len;

    if (len <= prev) return;
    const lastMsg = messages[len - 1];
    if (lastMsg?.role === 'user' || userScrolledUp.current) return;

    const justStreamed = streamingWasActive.current;
    streamingWasActive.current = false;
    if (justStreamed) return; // streamed response — leave the view where it is

    // Non-streamed AI addition (history load) — scroll to show it
    requestAnimationFrame(() => {
      const container = ref.current;
      if (!container) return;
      const allMsgs = container.querySelectorAll('.chat-msg');
      const lastMsgEl = allMsgs[allMsgs.length - 1] as HTMLElement | undefined;
      if (lastMsgEl) scrollToEl(container, lastMsgEl);
    });
  }, [messages, scrollToEl]);

  // ── Streaming: track only, no auto-follow ─────────────────────────────────
  // The response no longer drags the view down as it streams (or once it
  // finishes) — the view stays wherever the user left it (e.g. pinned to
  // their question above). streamingWasActive is still recorded so the
  // AI-message effect above can tell "just finished streaming" apart from
  // a genuine history load and skip scrolling for the former.
  useLayoutEffect(() => {
    if (!streamingContent) return;
    streamingWasActive.current = true;
  }, [streamingContent]);

  return ref;
}
